import React, { useEffect, useState } from 'react';
import { SettingsIcon, GlobeIcon, UsersIcon, PaletteIcon, SaveIcon, CheckCircleIcon, AlertCircleIcon, XIcon, PlusIcon, TrashIcon, EditIcon } from 'lucide-react';
import { useSiteContent } from '../../contexts/SiteContentContext';
const Toast = ({
  message,
  type,
  onClose
}: {
  message: string;
  type: 'success' | 'error';
  onClose: () => void;
}) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);
  return <div className={`fixed top-20 right-8 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-white animate-fadeIn ${type === 'success' ? 'bg-green-600' : 'bg-red-600'}`} data-id="element-2413">
      {type === 'success' ? <CheckCircleIcon size={20} data-id="element-2414" /> : <AlertCircleIcon size={20} data-id="element-2415" />}
      <span className="font-medium text-sm" data-id="element-2416">{message}</span>
      <button onClick={onClose} className="ml-4 hover:opacity-80" data-id="element-2417">
        <XIcon size={16} data-id="element-2418" />
      </button>
    </div>;
};
const SettingsManager = () => {
  const {
    settings,
    updateSettings,
    updateSeoSettings
  } = useSiteContent();
  const [activeTab, setActiveTab] = useState('general');
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  // Local form states initialized from shared context
  const [generalSettings, setGeneralSettings] = useState({
    schoolName: settings.schoolName,
    tagline: settings.tagline,
    email: settings.email,
    phone: settings.phone,
    address: settings.address
  });
  const [seoPage, setSeoPage] = useState('home');
  const [seoSettings, setSeoSettings] = useState({
    page: 'home',
    title: settings.seo.home.title,
    description: settings.seo.home.description,
    keywords: settings.seo.home.keywords
  });
  const [appearanceSettings, setAppearanceSettings] = useState({
    primaryColor: settings.primaryColor,
    accentColor: settings.accentColor,
    logoUrl: settings.logoUrl,
    darkModeEnabled: settings.darkModeEnabled
  });
  const [adminUsers, setAdminUsers] = useState([{
    id: 1,
    name: 'Super Admin',
    email: 'admin@igs.edu.ng',
    role: 'Super Admin',
    lastLogin: 'Today, 10:23 AM'
  }, {
    id: 2,
    name: 'Content Editor',
    email: 'editor@igs.edu.ng',
    role: 'Editor',
    lastLogin: 'Yesterday, 2:15 PM'
  }, {
    id: 3,
    name: 'Alumni Manager',
    email: 'alumni@igs.edu.ng',
    role: 'Moderator',
    lastLogin: 'Feb 18, 2026'
  }]);
  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({
      message,
      type
    });
  };
  const handleSave = () => {
    if (activeTab === 'general') {
      updateSettings({
        schoolName: generalSettings.schoolName,
        tagline: generalSettings.tagline,
        email: generalSettings.email,
        phone: generalSettings.phone,
        address: generalSettings.address
      });
      showToast('General settings saved — changes are live on website');
    } else if (activeTab === 'seo') {
      updateSeoSettings(seoSettings.page, {
        title: seoSettings.title,
        description: seoSettings.description,
        keywords: seoSettings.keywords
      });
      showToast('SEO settings saved — changes are live on website');
    } else if (activeTab === 'appearance') {
      updateSettings({
        primaryColor: appearanceSettings.primaryColor,
        accentColor: appearanceSettings.accentColor,
        logoUrl: appearanceSettings.logoUrl,
        darkModeEnabled: appearanceSettings.darkModeEnabled
      });
      showToast('Appearance settings saved — changes are live on website');
    }
  };
  const handleDeleteUser = (id: number) => {
    if (id === 1) {
      showToast('Cannot delete the primary Super Admin account', 'error');
      return;
    }
    setAdminUsers(adminUsers.filter(u => u.id !== id));
    showToast('User deleted successfully');
  };
  const tabs = [{
    id: 'general',
    label: 'General',
    icon: <SettingsIcon size={18} data-id="element-2419" />
  }, {
    id: 'seo',
    label: 'SEO & Meta',
    icon: <GlobeIcon size={18} data-id="element-2420" />
  }, {
    id: 'users',
    label: 'Admin Users',
    icon: <UsersIcon size={18} data-id="element-2421" />
  }, {
    id: 'appearance',
    label: 'Appearance',
    icon: <PaletteIcon size={18} data-id="element-2422" />
  }];
  return <div className="space-y-6 animate-fadeIn relative" data-id="element-2423">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} data-id="element-2424" />}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-2425">
        <div data-id="element-2426">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-2427">System Settings</h1>
          <p className="text-sm text-gray-500" data-id="element-2428">
            Manage global configurations and preferences.
          </p>
        </div>
        {activeTab !== 'users' && <button onClick={handleSave} className="inline-flex items-center px-4 py-2 bg-blue-950 text-yellow-400 rounded-lg text-sm font-medium hover:bg-blue-900 shadow-sm transition-colors" data-id="element-2429">
            <SaveIcon size={16} className="mr-2" data-id="element-2430" />
            Save Changes
          </button>}
        {activeTab === 'users' && <button onClick={() => showToast('User invite sent successfully')} className="inline-flex items-center px-4 py-2 bg-blue-950 text-yellow-400 rounded-lg text-sm font-medium hover:bg-blue-900 shadow-sm transition-colors" data-id="element-2431">
            <PlusIcon size={16} className="mr-2" data-id="element-2432" />
            Invite User
          </button>}
      </div>

      <div className="flex flex-col md:flex-row gap-6" data-id="element-2433">
        {/* Settings Sidebar */}
        <div className="w-full md:w-64 flex-shrink-0" data-id="element-2434">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-2435">
            <ul className="divide-y divide-gray-100" data-id="element-2436">
              {tabs.map(tab => <li key={tab.id} data-id="element-2437">
                  <button onClick={() => setActiveTab(tab.id)} className={`w-full flex items-center gap-3 px-4 py-4 text-sm font-medium transition-colors ${activeTab === tab.id ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-600' : 'text-gray-600 hover:bg-gray-50 border-l-4 border-transparent'}`} data-id="element-2438">
                    {tab.icon}
                    {tab.label}
                  </button>
                </li>)}
            </ul>
          </div>
        </div>

        {/* Settings Content */}
        <div className="flex-1 bg-white rounded-xl shadow-sm border border-gray-100 p-6 md:p-8" data-id="element-2439">
          {/* General Settings */}
          {activeTab === 'general' && <div className="max-w-2xl space-y-6 animate-fadeIn" data-id="element-2440">
              <h2 className="text-lg font-bold text-gray-900 border-b border-gray-200 pb-4" data-id="element-2441">
                General Information
              </h2>
              <div className="space-y-4" data-id="element-2442">
                <div data-id="element-2443">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2444">
                    School Name
                  </label>
                  <input type="text" value={generalSettings.schoolName} onChange={e => setGeneralSettings({
                ...generalSettings,
                schoolName: e.target.value
              })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" data-id="element-2445" />
                </div>
                <div data-id="element-2446">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2447">
                    Tagline / Motto
                  </label>
                  <input type="text" value={generalSettings.tagline} onChange={e => setGeneralSettings({
                ...generalSettings,
                tagline: e.target.value
              })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" data-id="element-2448" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2449">
                  <div data-id="element-2450">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2451">
                      Contact Email
                    </label>
                    <input type="email" value={generalSettings.email} onChange={e => setGeneralSettings({
                  ...generalSettings,
                  email: e.target.value
                })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" data-id="element-2452" />
                  </div>
                  <div data-id="element-2453">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2454">
                      Phone Number
                    </label>
                    <input type="tel" value={generalSettings.phone} onChange={e => setGeneralSettings({
                  ...generalSettings,
                  phone: e.target.value
                })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" data-id="element-2455" />
                  </div>
                </div>
                <div data-id="element-2456">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2457">
                    Physical Address
                  </label>
                  <textarea rows={3} value={generalSettings.address} onChange={e => setGeneralSettings({
                ...generalSettings,
                address: e.target.value
              })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none resize-none" data-id="element-2458"></textarea>
                </div>
              </div>
            </div>}

          {/* SEO Settings */}
          {activeTab === 'seo' && <div className="max-w-2xl space-y-6 animate-fadeIn" data-id="element-2459">
              <div className="flex items-center justify-between border-b border-gray-200 pb-4" data-id="element-2460">
                <h2 className="text-lg font-bold text-gray-900" data-id="element-2461">
                  Search Engine Optimization
                </h2>
                <select value={seoSettings.page} onChange={e => setSeoSettings({
              ...seoSettings,
              page: e.target.value
            })} className="px-3 py-1.5 border border-gray-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-blue-500" data-id="element-2462">
                  <option value="home" data-id="element-2463">Homepage</option>
                  <option value="about" data-id="element-2464">About Page</option>
                  <option value="admissions" data-id="element-2465">Admissions Page</option>
                </select>
              </div>
              <div className="space-y-4" data-id="element-2466">
                <div data-id="element-2467">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2468">
                    Meta Title
                  </label>
                  <input type="text" value={seoSettings.title} onChange={e => setSeoSettings({
                ...seoSettings,
                title: e.target.value
              })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" data-id="element-2469" />
                  <p className="text-xs text-gray-500 mt-1" data-id="element-2470">
                    Recommended length: 50-60 characters
                  </p>
                </div>
                <div data-id="element-2471">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2472">
                    Meta Description
                  </label>
                  <textarea rows={3} value={seoSettings.description} onChange={e => setSeoSettings({
                ...seoSettings,
                description: e.target.value
              })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none resize-none" data-id="element-2473"></textarea>
                  <p className="text-xs text-gray-500 mt-1" data-id="element-2474">
                    Recommended length: 150-160 characters
                  </p>
                </div>
                <div data-id="element-2475">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2476">
                    Keywords (comma separated)
                  </label>
                  <input type="text" value={seoSettings.keywords} onChange={e => setSeoSettings({
                ...seoSettings,
                keywords: e.target.value
              })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" data-id="element-2477" />
                </div>
              </div>
            </div>}

          {/* Users Settings */}
          {activeTab === 'users' && <div className="space-y-6 animate-fadeIn" data-id="element-2478">
              <h2 className="text-lg font-bold text-gray-900 border-b border-gray-200 pb-4" data-id="element-2479">
                Admin Users
              </h2>
              <div className="overflow-x-auto" data-id="element-2480">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-2481">
                  <thead className="bg-gray-50" data-id="element-2482">
                    <tr data-id="element-2483">
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2484">
                        User
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2485">
                        Role
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2486">
                        Last Login
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2487">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-2488">
                    {adminUsers.map(user => <tr key={user.id} className="hover:bg-gray-50 transition-colors" data-id="element-2489">
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-2490">
                          <div className="flex items-center" data-id="element-2491">
                            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold" data-id="element-2492">
                              {user.name.charAt(0)}
                            </div>
                            <div className="ml-4" data-id="element-2493">
                              <div className="text-sm font-medium text-gray-900" data-id="element-2494">
                                {user.name}
                              </div>
                              <div className="text-sm text-gray-500" data-id="element-2495">
                                {user.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-2496">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${user.role === 'Super Admin' ? 'bg-purple-100 text-purple-800' : user.role === 'Editor' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`} data-id="element-2497">
                            {user.role}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2498">
                          {user.lastLogin}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-2499">
                          <button className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors mr-2" data-id="element-2500">
                            <EditIcon size={18} data-id="element-2501" />
                          </button>
                          <button onClick={() => handleDeleteUser(user.id)} className={`p-1.5 rounded transition-colors ${user.id === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:text-red-600 hover:bg-red-50'}`} disabled={user.id === 1} data-id="element-2502">
                            <TrashIcon size={18} data-id="element-2503" />
                          </button>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>}

          {/* Appearance Settings */}
          {activeTab === 'appearance' && <div className="max-w-2xl space-y-6 animate-fadeIn" data-id="element-2504">
              <h2 className="text-lg font-bold text-gray-900 border-b border-gray-200 pb-4" data-id="element-2505">
                Brand & Appearance
              </h2>
              <div className="space-y-6" data-id="element-2506">
                <div data-id="element-2507">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2508">
                    Brand Logo URL
                  </label>
                  <div className="flex gap-4 items-start" data-id="element-2509">
                    <div className="flex-1" data-id="element-2510">
                      <input type="url" value={appearanceSettings.logoUrl} onChange={e => setAppearanceSettings({
                    ...appearanceSettings,
                    logoUrl: e.target.value
                  })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" data-id="element-2511" />
                    </div>
                    <div className="w-16 h-16 bg-gray-100 rounded border border-gray-200 flex items-center justify-center overflow-hidden" data-id="element-2512">
                      <span className="text-xs text-gray-400" data-id="element-2513">Preview</span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-2514">
                  <div data-id="element-2515">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2516">
                      Primary Color
                    </label>
                    <div className="flex items-center gap-3" data-id="element-2517">
                      <input type="color" value={appearanceSettings.primaryColor} onChange={e => setAppearanceSettings({
                    ...appearanceSettings,
                    primaryColor: e.target.value
                  })} className="w-10 h-10 rounded cursor-pointer" data-id="element-2518" />
                      <input type="text" value={appearanceSettings.primaryColor} onChange={e => setAppearanceSettings({
                    ...appearanceSettings,
                    primaryColor: e.target.value
                  })} className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none font-mono text-sm uppercase" data-id="element-2519" />
                    </div>
                  </div>
                  <div data-id="element-2520">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2521">
                      Accent Color
                    </label>
                    <div className="flex items-center gap-3" data-id="element-2522">
                      <input type="color" value={appearanceSettings.accentColor} onChange={e => setAppearanceSettings({
                    ...appearanceSettings,
                    accentColor: e.target.value
                  })} className="w-10 h-10 rounded cursor-pointer" data-id="element-2523" />
                      <input type="text" value={appearanceSettings.accentColor} onChange={e => setAppearanceSettings({
                    ...appearanceSettings,
                    accentColor: e.target.value
                  })} className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none font-mono text-sm uppercase" data-id="element-2524" />
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-100" data-id="element-2525">
                  <label className="flex items-center justify-between cursor-pointer" data-id="element-2526">
                    <div data-id="element-2527">
                      <span className="block text-sm font-medium text-gray-900" data-id="element-2528">
                        Enable Dark Mode Toggle
                      </span>
                      <span className="block text-sm text-gray-500" data-id="element-2529">
                        Allow users to switch between light and dark themes
                      </span>
                    </div>
                    <div className="relative" data-id="element-2530">
                      <input type="checkbox" className="sr-only" checked={appearanceSettings.darkModeEnabled} onChange={e => setAppearanceSettings({
                    ...appearanceSettings,
                    darkModeEnabled: e.target.checked
                  })} data-id="element-2531" />
                      <div className={`block w-14 h-8 rounded-full transition-colors ${appearanceSettings.darkModeEnabled ? 'bg-blue-600' : 'bg-gray-300'}`} data-id="element-2532"></div>
                      <div className={`absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition-transform ${appearanceSettings.darkModeEnabled ? 'transform translate-x-6' : ''}`} data-id="element-2533"></div>
                    </div>
                  </label>
                </div>
              </div>
            </div>}
        </div>
      </div>
    </div>;
};
export default SettingsManager;